/**
 * SearchPanel - 텍스트 검색 기능
 *
 * EPUB/PDF 콘텐츠 내 텍스트 검색
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { useState, useEffect, useRef } from 'react';
import { FiSearch, FiX, FiChevronUp, FiChevronDown } from 'react-icons/fi';

interface SearchResult {
  id: string;
  text: string;
  excerpt: string;
  cfi?: string;      // EPUB CFI
  pageNumber?: number; // PDF page
}

interface SearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  fileType: 'epub' | 'pdf';
  onNavigate: (result: SearchResult) => void;
}

const SearchPanel = ({ isOpen, onClose, fileType, onNavigate }: SearchPanelProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle search
  const handleSearch = async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setSearching(true);
    const searchResults: SearchResult[] = [];

    try {
      if (fileType === 'epub') {
        // PM 결정(2026-09-14): EPUB도 책 전체를 훑는다.
        // 종전엔 epubGetCurrentText()로 "지금 렌더된 챕터" 하나만 봐서, 다른 챕터에
        // 있는 단어는 검색해도 결과가 0건이었다(PDF는 S2-2에서 이미 해결된 같은 결함).
        // 스크롤 모드(EPUBViewer.tsx)는 spine을 훑어 이동용 href를 cfi에 실어 주고,
        // 책넘김 모드(Reader.tsx)는 flipbook 페이지 번호를 실어 준다 — 클릭 이동은
        // Reader.tsx의 onNavigate가 둘 다 처리한다.
        const matches = (await window.epubSearchAllPages?.(query)) || [];
        matches.forEach((m, idx) => {
          searchResults.push({
            id: `result-${idx}`,
            text: m.text,
            excerpt: m.excerpt,
            cfi: m.cfi || undefined,
            pageNumber: m.pageNumber,
          });
        });

      } else {
        // PDF 검색 — S2-2: 전체 문서를 훑는다.
        // 예전엔 window.pdfGetCurrentText()로 "지금 보고 있는 페이지"만 봤다 —
        // 그러면 검색 결과가 전부 현재 페이지에서만 나와 클릭해도 어디로도 안
        // 움직이는 것과 마찬가지였다(항상 이미 그 페이지에 있으므로).
        // window.pdfSearchAllPages()가 PDFViewer.tsx에서 페이지별로 캐시해 가며
        // 문서 전체를 검색해 (페이지, 발췌문)을 돌려준다.
        const matches = (await window.pdfSearchAllPages?.(query)) || [];

        matches.forEach((m, idx) => {
          searchResults.push({
            id: `result-${idx}`,
            text: m.text,
            excerpt: m.excerpt,
            pageNumber: m.pageNumber,
          });
        });
      }
    } catch (err) {
      console.error('검색 오류:', err);
    }

    setResults(searchResults);
    setCurrentIndex(0);
    setSearching(false);
  };

  // Navigate to result
  const goToResult = (index: number) => {
    if (results[index]) {
      setCurrentIndex(index);
      onNavigate(results[index]);
    }
  };

  // Keyboard shortcuts — B-f: ↑/↓로 결과 사이를 이동(미리보기만, 이동은 없음), Enter로 선택된 결과에 점프
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      if (results.length > 0) {
        e.preventDefault();
        setCurrentIndex(i => (i + 1) % results.length);
      }
    } else if (e.key === 'ArrowUp') {
      if (results.length > 0) {
        e.preventDefault();
        setCurrentIndex(i => (i - 1 + results.length) % results.length);
      }
    } else if (e.key === 'Enter') {
      if (results.length > 0) {
        goToResult(currentIndex);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute top-0 left-0 right-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg z-50">
      <div className="flex items-center gap-2 p-2">
        {/* Search Input */}
        <div className="flex-1 flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
          <FiSearch className="text-gray-400" size={18} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="검색어를 입력하세요..."
            className="flex-1 bg-transparent outline-none text-sm dark:text-white"
          />
          {query && (
            <button onClick={() => { setQuery(''); setResults([]); }} className="text-gray-400 hover:text-gray-600">
              <FiX size={16} />
            </button>
          )}
        </div>

        {/* Result Count */}
        {results.length > 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {currentIndex + 1} / {results.length}
          </span>
        )}

        {/* Navigation */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToResult(Math.max(0, currentIndex - 1))}
            disabled={results.length === 0}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            title="이전 결과"
          >
            <FiChevronUp size={18} />
          </button>
          <button
            onClick={() => goToResult((currentIndex + 1) % results.length)}
            disabled={results.length === 0}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            title="다음 결과"
          >
            <FiChevronDown size={18} />
          </button>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          title="닫기"
        >
          <FiX size={18} />
        </button>
      </div>

      {/* Search Results List */}
      {results.length > 0 && (
        <div className="max-h-60 overflow-y-auto border-t border-gray-100 dark:border-gray-700">
          {results.map((result, idx) => (
            <button
              key={result.id}
              onClick={() => goToResult(idx)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                idx === currentIndex ? 'bg-purple-50 dark:bg-purple-900/30' : ''
              }`}
            >
              <p className="text-gray-600 dark:text-gray-300 line-clamp-2">
                {result.excerpt.split(new RegExp(`(${query})`, 'gi')).map((part, i) =>
                  part.toLowerCase() === query.toLowerCase()
                    ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-600 px-0.5 rounded">{part}</mark>
                    : part
                )}
              </p>
              {result.pageNumber && (
                <span className="text-xs text-gray-400">{result.pageNumber}페이지</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {query && results.length === 0 && !searching && (
        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center border-t border-gray-100 dark:border-gray-700">
          "{query}"에 대한 검색 결과가 없습니다
        </div>
      )}

      {/* Searching */}
      {searching && (
        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center border-t border-gray-100 dark:border-gray-700">
          검색 중...
        </div>
      )}
    </div>
  );
};

export default SearchPanel;
